import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, TrendingUp, Users, Calendar, AlertTriangle, CheckCircle, ArrowLeft } from 'lucide-react'

export default function ProjectDashboardPage() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState([
    { id: 1, name: 'Website Redesign', status: 'On Track', progress: 75, team: 'Design Team', dueDate: '2026-06-30', members: 5, priority: 'High' },
    { id: 2, name: 'Mobile App Launch', status: 'Nearly Done', progress: 90, team: 'Dev Team', dueDate: '2026-05-15', members: 8, priority: 'Critical' },
    { id: 3, name: 'API Integration', status: 'At Risk', progress: 45, team: 'Backend Team', dueDate: '2026-05-30', members: 3, priority: 'High' },
    { id: 4, name: 'Documentation', status: 'On Track', progress: 60, team: 'Tech Writers', dueDate: '2026-07-15', members: 2, priority: 'Medium' },
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingProject, setEditingProject] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    status: 'On Track',
    progress: 0,
    team: '',
    dueDate: '',
    members: 0,
    priority: 'Medium'
  })

  const stats = useMemo(() => ({
    total: projects.length,
    onTrack: projects.filter(p => p.status === 'On Track').length,
    atRisk: projects.filter(p => p.status === 'At Risk').length,
    avgProgress: Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length)
  }), [projects])

  const handleCreateProject = () => {
    setEditingProject(null)
    setFormData({ name: '', status: 'On Track', progress: 0, team: '', dueDate: '', members: 0, priority: 'Medium' })
    setShowModal(true)
  }

  const handleEditProject = (project) => {
    setEditingProject(project)
    setFormData(project)
    setShowModal(true)
  }

  const handleSaveProject = () => {
    if (!formData.name) { alert('Project name required'); return }

    if (editingProject) {
      setProjects(projects.map(p => p.id === editingProject.id ? { ...p, ...formData } : p))
    } else {
      setProjects([...projects, { id: Date.now(), ...formData }])
    }
    setShowModal(false)
  }

  const handleDeleteProject = (id) => {
    if (confirm('Delete this project?')) {
      setProjects(projects.filter(p => p.id !== id))
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Track': return 'bg-green-600/20 text-green-400'
      case 'At Risk': return 'bg-red-600/20 text-red-400'
      case 'Nearly Done': return 'bg-blue-600/20 text-blue-400'
      default: return 'bg-slate-600/20 text-slate-400'
    }
  }

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'from-green-500 to-green-600'
    if (progress >= 50) return 'from-yellow-500 to-yellow-600'
    return 'from-orange-500 to-orange-600'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Project Management
        </button>
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Project Dashboard</h1>
              <p className="text-slate-400">Overview of all active projects and team progress</p>
            </div>
            <button onClick={handleCreateProject} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
              <Plus size={20} /> New Project
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Projects</div>
            <div className="text-3xl font-bold text-blue-400">{stats.total}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">On Track</div>
            <div className="text-3xl font-bold text-green-400">{stats.onTrack}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">At Risk</div>
            <div className="text-3xl font-bold text-red-400">{stats.atRisk}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg Progress</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.avgProgress}%</div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="space-y-4">
          {projects.map((project) => (
            <div key={project.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{project.name}</h3>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusColor(project.status)}`}>{project.status}</span>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${project.priority === 'Critical' ? 'bg-red-600/20 text-red-400' : project.priority === 'High' ? 'bg-orange-600/20 text-orange-400' : 'bg-blue-600/20 text-blue-400'}`}>{project.priority}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">{project.team}</p>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-400">Progress</span>
                      <span className="text-sm font-bold text-white">{project.progress}%</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div className={`h-full rounded-full bg-gradient-to-r ${getProgressColor(project.progress)}`} style={{ width: `${project.progress}%` }}></div>
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Calendar size={16} /> {new Date(project.dueDate).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Users size={16} /> {project.members} members
                    </div>
                    <div className="text-slate-400">Completion: {project.progress}%</div>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleEditProject(project)} className="p-2 hover:bg-blue-500/20 rounded transition-all">
                    <Edit2 size={18} className="text-blue-400" />
                  </button>
                  <button onClick={() => handleDeleteProject(project.id)} className="p-2 hover:bg-red-500/20 rounded transition-all">
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">{editingProject ? 'Edit Project' : 'Create New Project'}</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Project Name" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.team} onChange={(e) => setFormData({...formData, team: e.target.value})} placeholder="Team Name" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="date" value={formData.dueDate} onChange={(e) => setFormData({...formData, dueDate: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                  <input type="number" value={formData.members} onChange={(e) => setFormData({...formData, members: parseInt(e.target.value)})} placeholder="Team Members" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>On Track</option>
                    <option>At Risk</option>
                    <option>Nearly Done</option>
                  </select>
                  <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                    <option>Critical</option>
                  </select>
                  <input type="number" value={formData.progress} onChange={(e) => setFormData({...formData, progress: parseInt(e.target.value)})} placeholder="Progress %" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" min="0" max="100" />
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSaveProject} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Save Project</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
